import React, { useState, useEffect } from 'react';
import { DraggableModal } from "@/components/ui/draggable-modal";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

const LeaveActionDialog = ({
  open,
  onOpenChange,
  selectedLeaveRequest,
  leaveAction,
  userRole,
  personnelId,
  filterApprovalHistory,
  onAction
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localComment, setLocalComment] = useState("");

  // Dialog kapandığında loading state'ini sıfırla
  useEffect(() => {
    if (!open) {
      setIsSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setLocalComment("");
    }
  }, [open, selectedLeaveRequest, leaveAction]);

  const handleAction = async () => {
    if (isSubmitting) return; // Double-click önleme
    setIsSubmitting(true);
    
    // Timeout ekle - 30 saniye sonra loading'i kapat ve dialog'u kapat
    const timeoutId = setTimeout(() => {
      console.warn("⚠️ İzin talebi işlemi timeout - loading kapatılıyor");
      setIsSubmitting(false);
      onOpenChange(false); // Dialog'u kapat
    }, 30000);
    
    try {
      await onAction(localComment);
      clearTimeout(timeoutId);
      // Başarılı olduğunda loading'i kapat ve dialog'u kapat
      setIsSubmitting(false);
      // onAction içinde dialog kapatılıyor ama güvenlik için burada da kapat
      setTimeout(() => {
        onOpenChange(false);
      }, 100);
    } catch (error) {
      clearTimeout(timeoutId);
      // Hata durumunda loading'i kapat ama dialog'u açık tut
      console.error("Leave action error:", error);
      setIsSubmitting(false);
      // Dialog açık kalsın - kullanıcı tekrar deneyebilsin
    }
  };

  return (
    <DraggableModal
      isOpen={open}
      onClose={() => !isSubmitting && onOpenChange(false)}
      title={
        leaveAction === "approve" ? "İzin Talebini Onayla" :
        leaveAction === "reject" ? "İzin Talebini Reddet" :
        "Üst Makama Sevk Et"
      }
      maxWidth="max-w-md"
    >
        {selectedLeaveRequest && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded">
              <p className="font-semibold">{selectedLeaveRequest.personnel_name}</p>
              <p className="text-sm text-gray-600">
                {new Date(selectedLeaveRequest.start_date).toLocaleString('tr-TR', { hour12: false })} - {new Date(selectedLeaveRequest.end_date).toLocaleString('tr-TR', { hour12: false })}
              </p>
              <p className="text-sm mt-2">
                <span className="font-medium">İzin Türü:</span> {selectedLeaveRequest.reason_type || selectedLeaveRequest.reason}
              </p>
              {selectedLeaveRequest.reason_detail && (
                <div className="mt-2 p-2 bg-blue-50 border-l-4 border-blue-400 rounded">
                  <p className="text-xs font-semibold text-blue-800">Personel Açıklaması:</p>
                  <p className="text-xs text-blue-700 italic mt-1">{selectedLeaveRequest.reason_detail}</p>
                </div>
              )}
            </div>
            
            {/* Tüm Approval History - Filtrelenmiş */}
            {(() => {
              const filteredHistory = filterApprovalHistory(
                selectedLeaveRequest.approval_history, 
                userRole, 
                selectedLeaveRequest.status,
                personnelId,
                selectedLeaveRequest.personnel_id
              );
              return filteredHistory.length > 0 && (
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs font-semibold text-gray-700 mb-2">İşlem Geçmişi:</p>
                  <div className="space-y-2">
                    {filteredHistory.map((step, index) => (
                      <div key={index} className="p-2 bg-white border border-gray-200 rounded text-xs">
                        <p className="font-semibold text-gray-800">
                          {step.approver_name} - {
                            step.action === "approve" || step.action === "approved" ? "✅ Onayladı" :
                            step.action === "reject" || step.action === "rejected" ? "❌ Reddetti" :
                            step.action === "reject_and_forward" ? "❌ Reddet ile Üst Makama Sevk Etti." :
                            step.action === "forward" || step.action === "escalate" ? "⬆️ Üst Makama Sevk Etti" :
                            "⬆️ Üst Makama Sevk Etti"
                          }
                        </p>
                        {step.comment && (
                          <p className="text-gray-600 italic mt-1">&ldquo;{step.comment}&rdquo;</p>
                        )}
                        <p className="text-gray-400 text-[10px] mt-1">
                          {new Date(step.timestamp).toLocaleString('tr-TR')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            <div>
              <Label>Açıklama (Opsiyonel)</Label>
              <Textarea
                value={localComment}
                onChange={(e) => setLocalComment(e.target.value)}
                placeholder="Karar gerekçenizi yazın..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                İptal
              </Button>
              <Button
                onClick={handleAction}
                disabled={isSubmitting}
                data-testid="leave-action-confirm"
                className={
                  leaveAction === "approve" ? "bg-green-600 hover:bg-green-700" :
                  leaveAction === "reject" ? "bg-red-600 hover:bg-red-700" :
                  "bg-blue-600 hover:bg-blue-700"
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    İşleniyor...
                  </>
                ) : (
                  <>
                    {leaveAction === "approve" && "Onayla"}
                    {leaveAction === "reject" && "Reddet"}
                    {leaveAction === "escalate" && "Üst Makama Sevk Et"}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
    </DraggableModal>
  );
};

export default LeaveActionDialog;
